// Adapter for the production /api/admin/* surface.
//
// The backend payloads use snake_case + nested shapes that the v2 UI
// does not consume directly. Every fetch passes through a normalizer so
// the React components can rely on a single, stable, camelCase-ish
// shape — preventing the kind of `(c ?? []).find is not a function`
// crashes we saw on the Admin overview tab (queues was a dict, not an
// array, in the raw response).

import { apiCall, apiSoft } from "./http";

// ───────────────── Public types (what the UI consumes) ─────────────────

export interface SystemHealth {
  refresh_interval_ms?: number;
  workers?: {
    dyno: string;
    mem_mb?: number;
    mem_pct?: number;
    cpu_pct?: number;
    last_heartbeat?: string;
    stale?: boolean;
  }[];
  queues?: { name: string; depth?: number }[];
  queue_pressure?: string;
  ai_throttle?: { state?: string; remaining?: number; limit?: number };
  db_pool?: { used?: number; size?: number; saturation_pct?: number };
  active_jobs?: { job_id: string; pack?: string; pct?: number; status?: string }[];
  totals_24h?: { total?: number; completed?: number; failed?: number; cancelled?: number };
  redis?: { connected?: boolean; clients?: number };
  postgres?: { connected?: boolean };
  hyper_files?: { count?: number; total_mb?: number };
}

export interface AdminStats {
  totals?: {
    total?: number;
    completed?: number;
    failed?: number;
    cancelled?: number;
    success_rate_pct?: number;
    avg_duration_seconds?: number;
  };
  daily?: { date: string; completed: number; failed: number }[];
  by_volume?: { volume: string; avg_seconds: number }[];
  slack_configured?: boolean;
}

export interface ActivityRow {
  job_id: string;
  user_email?: string;
  pack?: string;
  volume?: string;
  status?: string;
  duration?: string | number | null;
  output_mode?: string;
  created_at?: string;
}

export interface CuratorRow {
  id: string;
  email: string;
  name?: string;
  role: "curator" | "admin";
  has_password?: boolean;
  created_at?: string;
}

export interface ErrorRow {
  job_id: string;
  user_email?: string;
  pack?: string;
  error_message?: string;
  failed_at?: string;
}

export interface PackageInfo {
  version?: string;
  installed_packs?: number;
}

export interface FailureTrend {
  by_category?: { category: string; count: number }[];
  total?: number;
  days?: number;
}

export interface PhaseTimings {
  phases?: {
    step: string;
    median_seconds?: number;
    p95_seconds?: number;
    recent_median_seconds?: number;
    delta_pct?: number;
    regressed?: boolean;
  }[];
}

export interface AuditEntry {
  id: string;
  actor?: string;
  action?: string;
  target?: string;
  detail?: string;
  created_at?: string;
}

export interface OrgConnection {
  id: string;
  org_name?: string;
  instance_url?: string;
  user_email?: string;
  created_at?: string;
}

export interface ResetResult {
  deleted?: number;
  would_delete?: number;
  packs?: number;
  notifs?: number;
  message?: string;
}

// ───────────────── Normalizers ─────────────────

type AnyRec = Record<string, unknown>;

function num(v: unknown): number | undefined {
  if (v == null) return undefined;
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function normalizeHealth(raw: AnyRec | null | undefined): SystemHealth {
  if (!raw || typeof raw !== "object") return {};

  // Workers: backend exposes worker_processes[]; keep `workers` alias too.
  const wpRaw = (raw.worker_processes ?? raw.workers ?? []) as AnyRec[];
  const workers = Array.isArray(wpRaw)
    ? wpRaw.map((w) => ({
        dyno: String(w.dyno ?? ""),
        mem_mb: num(w.mem_mb),
        mem_pct: num(w.mem_pct),
        cpu_pct: num(w.cpu_pct),
        stale: Boolean(w.stale),
      }))
    : [];

  // Queues: backend exposes queues:{high:{queued,name},default:{queued,name}}.
  // UI expects an array of {name, depth}. Always coerce.
  const qRaw = raw.queues as AnyRec | AnyRec[] | undefined;
  let queues: { name: string; depth?: number }[] = [];
  if (Array.isArray(qRaw)) {
    queues = qRaw.map((q) => ({
      name: String(q.name ?? ""),
      depth: num(q.depth ?? q.queued),
    }));
  } else if (qRaw && typeof qRaw === "object") {
    queues = Object.entries(qRaw).map(([key, val]) => ({
      name: key,
      depth: num((val as AnyRec | undefined)?.queued ?? (val as AnyRec | undefined)?.depth),
    }));
  }

  // Active jobs: backend uses key `pct`; pack already populated.
  const aj = (raw.active_jobs ?? []) as AnyRec[];
  const active_jobs = Array.isArray(aj)
    ? aj.map((j) => ({
        job_id: String(j.job_id ?? ""),
        pack: (j.pack as string) ?? "",
        pct: num(j.pct),
        status: (j.status as string) ?? "",
      }))
    : [];

  // 24h totals come from `recent_24h`.
  const r24 = (raw.recent_24h ?? raw.totals_24h ?? {}) as AnyRec;
  const totals_24h = {
    total: num(r24.total),
    completed: num(r24.completed),
    failed: num(r24.failed),
    cancelled: num(r24.cancelled),
  };

  // DB pool: backend exposes `postgres` for connectivity (no pool detail).
  // The web_process might carry pool stats; for now, surface connectivity.
  const pg = (raw.postgres ?? {}) as AnyRec;
  const dbPool = {
    used: num(pg.pool_used ?? pg.used),
    size: num(pg.pool_size ?? pg.size),
    saturation_pct: num(pg.saturation_pct),
  };

  const ai = (raw.ai_throttle ?? {}) as AnyRec;
  const ai_throttle = {
    state: ai.state as string | undefined,
    remaining: num(ai.remaining),
    limit: num(ai.limit),
  };

  const redis = (raw.redis ?? {}) as AnyRec;
  const hf = (raw.hyper_files ?? {}) as AnyRec;

  return {
    refresh_interval_ms: num(raw.refresh_interval_ms),
    workers,
    queues,
    queue_pressure: (raw.queue_pressure as string) ?? "none",
    ai_throttle,
    db_pool: dbPool,
    active_jobs,
    totals_24h,
    redis: {
      connected: Boolean(redis.available ?? redis.connected),
      clients: num(redis.clients),
    },
    postgres: {
      connected: Boolean(pg.available ?? pg.connected),
    },
    hyper_files: {
      count: num(hf.count),
      total_mb: num(hf.total_mb),
    },
  };
}

function normalizeStats(raw: AnyRec | null | undefined): AdminStats {
  if (!raw || typeof raw !== "object") return {};
  const daily = Array.isArray(raw.daily_stats)
    ? (raw.daily_stats as AnyRec[]).map((d) => ({
        date: String(d.date ?? ""),
        completed: num(d.completed) ?? 0,
        failed: num(d.failed) ?? 0,
      }))
    : [];
  const by_volume = Array.isArray(raw.duration_by_volume)
    ? (raw.duration_by_volume as AnyRec[]).map((v) => ({
        volume: String(v.volume ?? ""),
        avg_seconds: (num(v.avg_minutes) ?? 0) * 60,
      }))
    : [];
  return {
    totals: {
      total: num(raw.total_jobs),
      completed: num(raw.completed),
      failed: num(raw.failed),
      cancelled: num(raw.cancelled),
      success_rate_pct: num(raw.success_rate_pct),
      avg_duration_seconds: num(raw.avg_duration_seconds),
    },
    daily,
    by_volume,
    slack_configured: Boolean(raw.slack_configured),
  };
}

function normalizeActivity(raw: unknown): ActivityRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((r: AnyRec) => ({
    job_id: String(r.job_id ?? ""),
    user_email: (r.user_email as string) ?? "",
    pack: (r.pack_name as string) ?? (r.pack as string) ?? "",
    volume: (r.volume as string) ?? "",
    status: (r.status as string) ?? "",
    duration: num(r.duration_seconds) ?? null,
    output_mode: (r.output_mode as string) ?? "crm",
    created_at: (r.created_at as string) ?? "",
  }));
}

function normalizeErrors(raw: unknown): ErrorRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((r: AnyRec) => ({
    job_id: String(r.job_id ?? ""),
    user_email: (r.user_email as string) ?? "",
    pack: (r.pack_name as string) ?? (r.pack as string) ?? "",
    error_message: (r.error_message as string) ?? "",
    failed_at: (r.completed_at as string) ?? (r.created_at as string) ?? "",
  }));
}

function normalizeCurators(raw: unknown): CuratorRow[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((r: AnyRec) => ({
    id: String(r.id ?? ""),
    email: String(r.email ?? ""),
    name: (r.name as string) ?? "",
    role: ((r.role as string) ?? "curator") as "curator" | "admin",
    created_at: (r.added_at as string) ?? (r.created_at as string) ?? "",
  }));
}

function normalizeCurator(raw: AnyRec | null | undefined): CuratorRow {
  const r = raw ?? {};
  return {
    id: String(r.id ?? ""),
    email: String(r.email ?? ""),
    name: (r.name as string) ?? "",
    role: ((r.role as string) ?? "curator") as "curator" | "admin",
  };
}

function normalizePackage(raw: AnyRec | null | undefined): PackageInfo {
  const r = raw ?? {};
  return {
    version: (r.demo_metadata_package_version_id as string) ?? (r.version as string) ?? undefined,
    installed_packs: num(r.installed_packs_count ?? r.installed_packs),
  };
}

function normalizeFailureTrends(raw: AnyRec | null | undefined): FailureTrend {
  const r = raw ?? {};
  const bc = r.by_category;
  let by_category: { category: string; count: number }[] = [];
  if (Array.isArray(bc)) {
    by_category = (bc as AnyRec[]).map((c) => ({
      category: String(c.category ?? ""),
      count: num(c.count) ?? 0,
    }));
  } else if (bc && typeof bc === "object") {
    by_category = Object.entries(bc as AnyRec).map(([cat, count]) => ({
      category: cat,
      count: num(count) ?? 0,
    }));
  }
  by_category.sort((a, b) => b.count - a.count);
  return {
    by_category,
    total: num(r.total),
    days: num(r.days),
  };
}

function normalizePhaseTimings(raw: AnyRec | null | undefined): PhaseTimings {
  const r = raw ?? {};
  const steps = (r.steps ?? r.phases ?? []) as AnyRec[];
  if (!Array.isArray(steps)) return { phases: [] };
  const phases = steps.map((s) => ({
    step: String(s.step ?? ""),
    median_seconds: num(s.median_seconds) ?? (num(s.median_ms) ?? 0) / 1000,
    p95_seconds: num(s.p95_seconds) ?? (num(s.p95_ms) ?? 0) / 1000,
    recent_median_seconds:
      num(s.recent_median_seconds) ?? (num(s.recent_median_ms) ?? 0) / 1000,
    delta_pct: num(s.delta_pct),
    regressed: Boolean(s.regressed),
  }));
  return { phases };
}

function normalizeAuditLog(raw: unknown): AuditEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((r: AnyRec) => ({
    id: String(r.id ?? ""),
    actor: (r.actor as string) ?? "",
    action: (r.action as string) ?? "",
    target: (r.target as string) ?? "",
    detail: (r.detail as string) ?? "",
    created_at: (r.created_at as string) ?? "",
  }));
}

function normalizeOrgConnections(raw: unknown): OrgConnection[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((c: AnyRec) => ({
    id: String(c.id ?? ""),
    org_name: (c.org_name as string) ?? "",
    instance_url: (c.instance_url as string) ?? "",
    user_email: (c.user_email as string) ?? "",
    created_at: (c.connected_at as string) ?? (c.created_at as string) ?? "",
  }));
}

function normalizeResetJobs(raw: AnyRec | null | undefined): ResetResult {
  const r = raw ?? {};
  return {
    deleted: num(r.jobs_deleted ?? r.deleted),
    notifs: num(r.notifications_deleted ?? r.notifs),
    would_delete: num(r.jobs_to_delete ?? r.would_delete),
    message: (r.message as string) ?? "",
  };
}

function normalizeResetPacks(raw: AnyRec | null | undefined): ResetResult {
  const r = raw ?? {};
  return {
    packs: num(r.packs_deleted ?? r.packs),
    would_delete: num(r.packs_to_delete ?? r.would_delete),
  };
}

function normalizePurge(raw: AnyRec | null | undefined): { purged?: number; cancelled?: number } {
  const r = raw ?? {};
  const pq = (r.purged_from_queues ?? r.purged ?? {}) as AnyRec | number;
  let purged = 0;
  if (typeof pq === "number") purged = pq;
  else if (pq && typeof pq === "object") {
    purged = Object.values(pq).reduce<number>((a, v) => a + (num(v) ?? 0), 0);
  }
  return {
    purged,
    cancelled: num(r.cancelled_db_jobs),
  };
}

// ───────────────── Public API ─────────────────

async function softNorm<T>(path: string, normalize: (raw: unknown) => T): Promise<T> {
  const raw = await apiSoft<unknown>(path);
  return normalize(raw);
}

export const adminApi = {
  systemHealth: () => softNorm<SystemHealth>("/api/admin/system-health", (r) => normalizeHealth(r as AnyRec)),
  stats: () => softNorm<AdminStats>("/api/admin/stats", (r) => normalizeStats(r as AnyRec)),
  activityFeed: () => softNorm<ActivityRow[]>("/api/admin/activity-feed", normalizeActivity),
  curators: () => softNorm<CuratorRow[]>("/api/admin/curators", normalizeCurators),
  addCurator: async (body: { email: string; name?: string; role: "curator" | "admin" }) => {
    const raw = await apiCall<AnyRec>("/api/admin/curators", { method: "POST", body });
    return normalizeCurator(raw);
  },
  removeCurator: (id: string) =>
    apiCall<void>(`/api/admin/curators/${id}`, { method: "DELETE" }),
  updateCuratorRole: async (id: string, role: "curator" | "admin") => {
    const raw = await apiCall<AnyRec>(`/api/admin/curators/${id}/role`, {
      method: "PATCH",
      body: { role },
    });
    return normalizeCurator(raw);
  },
  errors: () => softNorm<ErrorRow[]>("/api/admin/errors", normalizeErrors),
  packageInfo: () => softNorm<PackageInfo>("/api/admin/package", (r) => normalizePackage(r as AnyRec)),
  failureTrends: (days = 7) =>
    softNorm<FailureTrend>(`/api/admin/failure-trends?days=${days}`, (r) =>
      normalizeFailureTrends(r as AnyRec),
    ),
  phaseTimings: (days = 14) =>
    softNorm<PhaseTimings>(`/api/admin/phase-timings?days=${days}`, (r) =>
      normalizePhaseTimings(r as AnyRec),
    ),
  auditLog: (limit = 50) =>
    softNorm<AuditEntry[]>(`/api/admin/audit-log?limit=${limit}`, normalizeAuditLog),
  orgConnections: () =>
    softNorm<OrgConnection[]>("/api/admin/org-connections", normalizeOrgConnections),
  resetJobHistory: async (body: {
    confirm: boolean;
    keep_completed?: boolean;
    older_than_days?: number | null;
  }) => {
    const raw = await apiCall<AnyRec>("/api/admin/reset-job-history", { method: "POST", body });
    return normalizeResetJobs(raw);
  },
  resetDraftPacks: async (body: { confirm: boolean }) => {
    const raw = await apiCall<AnyRec>("/api/admin/reset-draft-packs", { method: "POST", body });
    return normalizeResetPacks(raw);
  },
  purgeQueues: async () => {
    const raw = await apiCall<AnyRec>("/api/admin/purge-queues", { method: "POST" });
    return normalizePurge(raw);
  },
  forceCancel: (jobId: string) =>
    apiCall<void>(`/api/jobs/${jobId}/force-cancel`, { method: "POST" }),
  batchProvision: (body: {
    org_connection_ids: string[];
    industry_pack_id: string;
    volume_preset?: string;
    include_sales_cloud?: boolean;
    include_service_cloud?: boolean;
  }) =>
    apiCall<{ jobs: { job_id: string; org_id: string }[] }>("/api/jobs/batch", {
      method: "POST",
      body,
    }),
};
